// Fetch wrapper for all portal API calls: same-origin cookies, JSON in/out,
// automatic one-shot refresh on 401, normalized ApiError.

export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

let onUnauthorized = null;

// AuthContext registers a callback so an expired session clears user state globally.
export function setOnUnauthorized(callback) {
  onUnauthorized = callback;
}

async function rawFetch(path, { method = 'GET', body } = {}) {
  const options = {
    method,
    credentials: 'include',
    headers: {},
  };
  if (body !== undefined) {
    options.headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }
  return fetch(path, options);
}

async function parseBody(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

const NO_RETRY_PATHS = ['/api/auth/login', '/api/auth/refresh', '/api/auth/logout'];

export async function apiFetch(path, { method = 'GET', body, _isRetry = false } = {}) {
  let response;
  try {
    response = await rawFetch(path, { method, body });
  } catch {
    throw new ApiError('Network error. Check your connection and try again.', 0, null);
  }

  if (response.status === 401 && !_isRetry && !NO_RETRY_PATHS.includes(path)) {
    const refreshed = await rawFetch('/api/auth/refresh', { method: 'POST' });
    if (refreshed.ok) {
      return apiFetch(path, { method, body, _isRetry: true });
    }
    if (onUnauthorized) onUnauthorized();
  }

  const data = await parseBody(response);
  if (!response.ok) {
    throw new ApiError(data.error || `Request failed (${response.status})`, response.status, data);
  }
  return data;
}
